/**
 * Alta y edicion de un proveedor.
 *
 * El mismo dialogo hace las dos cosas porque los campos son identicos: la unica
 * diferencia es que al editar se puede dar de baja, y eso vive abajo de todo,
 * separado del resto por una linea.
 *
 * `moneda_lista` es el campo que mas importa y por eso va arriba, junto al
 * nombre: es el valor por defecto con el que se cargan las compras de este
 * proveedor, y equivocarlo es el error caro de este sistema.
 */
import { useEffect, useState } from "react";
import type { Moneda } from "@tora/shared";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Importe } from "@/components/money";
import { SelectorMoneda } from "@/components/money/switch-moneda";
import { aNumero, type CambiosProveedor, type Numerico, type Proveedor } from "@/lib/tipos";

export interface FormProveedor {
  nombre: string;
  moneda_lista: Moneda;
  codigo: string;
  contacto: string;
  telefono: string;
  email: string;
  direccion: string;
  localidad: string;
  observaciones: string;
  /** Dias habiles de entrega, como se escribe. Reposicion lo usa (0140). */
  demora: string;
  activo: boolean;
}

const VACIO: FormProveedor = {
  nombre: "",
  moneda_lista: "USD",
  codigo: "",
  contacto: "",
  telefono: "",
  email: "",
  direccion: "",
  localidad: "",
  observaciones: "",
  demora: "3",
  activo: true,
};

function desdeProveedor(p: Proveedor): FormProveedor {
  return {
    nombre: p.nombre,
    moneda_lista: p.moneda_lista,
    codigo: p.codigo ?? "",
    contacto: p.contacto ?? "",
    telefono: p.telefono ?? "",
    email: p.email ?? "",
    direccion: p.direccion ?? "",
    localidad: p.localidad ?? "",
    observaciones: p.observaciones ?? "",
    demora: String(p.demora_dias_habiles ?? 3),
    activo: p.activo,
  };
}

/** Un campo de texto vacio se guarda como null, no como "". */
function texto(v: string): string | null {
  const limpio = v.trim();
  return limpio === "" ? null : limpio;
}

export function cambiosDesdeForm(f: FormProveedor): CambiosProveedor {
  return {
    nombre: f.nombre.trim(),
    moneda_lista: f.moneda_lista,
    codigo: texto(f.codigo),
    contacto: texto(f.contacto),
    telefono: texto(f.telefono),
    email: texto(f.email),
    direccion: texto(f.direccion),
    localidad: texto(f.localidad),
    observaciones: texto(f.observaciones),
    demora_dias_habiles: Number(f.demora.trim()),
    activo: f.activo,
  };
}

/** Lo unico que no se puede dejar pasar. El email solo si esta escrito. */
export function problemasDelForm(f: FormProveedor): Record<string, string> {
  const p: Record<string, string> = {};
  if (f.nombre.trim() === "") p.nombre = "El proveedor necesita un nombre.";
  if (f.email.trim() !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
    p.email = "Ese email no tiene formato válido.";
  }
  const demora = Number(f.demora.trim());
  if (f.demora.trim() === "" || !Number.isInteger(demora) || demora < 1 || demora > 60) {
    p.demora = "Entre 1 y 60 días hábiles.";
  }
  return p;
}

/**
 * El otro proveedor ACTIVO que ya se llama así, o null. Mismo criterio que la
 * base (0123): sin mayúsculas ni espacios de las puntas.
 */
export function nombreRepetido(
  nombre: string,
  existentes: ReadonlyArray<Pick<Proveedor, "id" | "nombre" | "activo">>,
  excepto: number | null,
): string | null {
  const buscado = nombre.trim().toLowerCase();
  if (buscado === "") return null;
  const otro = existentes.find(
    (p) => p.activo && p.id !== excepto && p.nombre.trim().toLowerCase() === buscado,
  );
  return otro ? otro.nombre : null;
}

export interface DialogProveedorProps {
  abierto: boolean;
  /** null = alta. */
  proveedor: Proveedor | null;
  /** Lo que se le debe. Para avisar antes de darlo de baja con saldo. */
  saldo?: { saldo_ars: Numerico; saldo_usd: Numerico } | null;
  /** Los proveedores que ya hay, para avisar un nombre repetido mientras se escribe. */
  existentes?: ReadonlyArray<Pick<Proveedor, "id" | "nombre" | "activo">>;
  guardando: boolean;
  onCerrar: () => void;
  onGuardar: (cambios: CambiosProveedor) => void;
}

export function DialogProveedor({
  abierto,
  proveedor,
  saldo = null,
  existentes = [],
  guardando,
  onCerrar,
  onGuardar,
}: DialogProveedorProps): JSX.Element {
  const [form, setForm] = useState<FormProveedor>(VACIO);
  const [tocado, setTocado] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setForm(proveedor ? desdeProveedor(proveedor) : VACIO);
    setTocado(false);
  }, [abierto, proveedor]);

  const problemas = problemasDelForm(form);
  const repetido = form.activo
    ? nombreRepetido(form.nombre, existentes, proveedor?.id ?? null)
    : null;
  // Darlo de baja con deuda: la deuda no se borra, pero el proveedor sale de
  // la lista y de los desplegables, y se pierde de vista lo que se le debe.
  const debeArs = aNumero(saldo?.saldo_ars);
  const debeUsd = aNumero(saldo?.saldo_usd);
  const bajaConSaldo =
    proveedor !== null &&
    proveedor.activo &&
    !form.activo &&
    (Math.abs(debeArs) > 0.009 || Math.abs(debeUsd) > 0.009);
  const hayProblemas = Object.keys(problemas).length > 0;

  function cambiar<K extends keyof FormProveedor>(campo: K, valor: FormProveedor[K]): void {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  function confirmar(): void {
    setTocado(true);
    if (hayProblemas) return;
    onGuardar(cambiosDesdeForm(form));
  }

  return (
    <Dialog open={abierto} onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{proveedor ? "Editar proveedor" : "Proveedor nuevo"}</DialogTitle>
          <DialogDescription>
            {proveedor
              ? "Los cambios valen para las compras que se carguen de acá en adelante."
              : "Con el nombre y la moneda de la lista alcanza. El resto se puede completar después."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <Campo label="Nombre" requerido error={tocado ? problemas.nombre : undefined}>
            <Input
              id="prov-nombre"
              value={form.nombre}
              onChange={(e) => cambiar("nombre", e.target.value)}
              placeholder="ELIT S.A."
              autoComplete="off"
              autoFocus
            />
          </Campo>
          {repetido ? (
            <Aviso tono="warning" compacto>
              Ya existe «{repetido}». Si es el mismo, cerrá esto y elegilo de la lista: dos con el
              mismo nombre parten la deuda en dos.
            </Aviso>
          ) : null}

          <div className="space-y-1.5">
            <Label>Moneda de la lista</Label>
            <div className="flex items-center gap-3">
              <SelectorMoneda
                valor={form.moneda_lista}
                onChange={(m) => cambiar("moneda_lista", m)}
                etiqueta="Moneda de la lista del proveedor"
              />
              <p className="text-xs text-muted-foreground">
                Con esta moneda arranca la carga de sus remitos.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Campo label="Contacto">
              <Input
                id="prov-contacto"
                value={form.contacto}
                onChange={(e) => cambiar("contacto", e.target.value)}
                placeholder="Nombre de quien atiende"
                autoComplete="off"
              />
            </Campo>
            <Campo label="Teléfono">
              <Input
                id="prov-telefono"
                type="tel"
                inputMode="tel"
                value={form.telefono}
                onChange={(e) => cambiar("telefono", e.target.value)}
                placeholder="11 5555 5555"
                autoComplete="off"
              />
            </Campo>
            <Campo label="Email" error={tocado ? problemas.email : undefined}>
              <Input
                id="prov-email"
                inputMode="email"
                value={form.email}
                onChange={(e) => cambiar("email", e.target.value)}
                placeholder="ventas@proveedor.com"
                autoComplete="off"
              />
            </Campo>
            <Campo label="Código interno">
              <Input
                id="prov-codigo"
                value={form.codigo}
                onChange={(e) => cambiar("codigo", e.target.value)}
                placeholder="Opcional"
                autoComplete="off"
              />
            </Campo>
            <Campo label="Dirección">
              <Input
                id="prov-direccion"
                value={form.direccion}
                onChange={(e) => cambiar("direccion", e.target.value)}
                autoComplete="off"
              />
            </Campo>
            <Campo label="Localidad">
              <Input
                id="prov-localidad"
                value={form.localidad}
                onChange={(e) => cambiar("localidad", e.target.value)}
                autoComplete="off"
              />
            </Campo>
          </div>

          <Campo
            label="Demora de entrega (días hábiles)"
            ayuda="Desde que se le pide hasta que llega. Reposición la usa para saber si lo que queda alcanza."
            error={tocado ? problemas.demora : undefined}
          >
            <Input
              id="prov-demora"
              inputMode="numeric"
              value={form.demora}
              onChange={(e) => cambiar("demora", e.target.value)}
              className="w-24"
              autoComplete="off"
            />
          </Campo>

          <Campo label="Observaciones">
            <Textarea
              id="prov-obs"
              rows={2}
              value={form.observaciones}
              onChange={(e) => cambiar("observaciones", e.target.value)}
              placeholder="Días de entrega, condiciones de pago, lo que haga falta recordar."
            />
          </Campo>

          {proveedor ? (
            <Checkbox
              id="prov-activo"
              label="Darlo de baja"
              ayuda="Deja de aparecer para elegir. Las compras y los productos que ya lo referencian no se tocan."
              checked={!form.activo}
              onChange={(e) => cambiar("activo", !e.target.checked)}
              className="border-t border-border pt-4"
            />
          ) : null}
          {bajaConSaldo ? (
            <Aviso tono="warning" compacto>
              Todavía {debeArs < 0 || debeUsd < 0 ? "tiene un saldo de" : "le debés"}{" "}
              {Math.abs(debeUsd) > 0.009 ? (
                <Importe monto={debeUsd} moneda="USD" tamano="sm" codigo={false} />
              ) : null}
              {Math.abs(debeUsd) > 0.009 && Math.abs(debeArs) > 0.009 ? " y " : null}
              {Math.abs(debeArs) > 0.009 ? (
                <Importe monto={debeArs} moneda="ARS" tamano="sm" codigo={false} />
              ) : null}
              . Darlo de baja no borra la deuda, pero lo saca de la lista: pagala o ajustala antes.
            </Aviso>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button type="button" cargando={guardando} onClick={confirmar}>
            {proveedor ? "Guardar" : "Crear proveedor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
